import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { PoleCentrumKosztowego } from '@/components/PoleCentrumKosztowego';
import { Button } from '@/components/ui/button';
import { Field, ModalError } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import { podgladKorektyMpk, wykonajKorekteMpk, type PodgladKorektyMpk } from '@/lib/authoring-api';
import { fetchCostCenterSuggestions, listEmployees } from '@/lib/employees-api';
import { useI18n } from '@/lib/i18n';
import { useOpoznione } from '@/lib/opoznienie';

/**
 * KOREKTA MPK W HISTORII WYDAŃ — sekcja edytora config-down (28.09.2026).
 *
 * ⚠️ ZAMÓWIONE: „potrzebujemy poprawić wstecz numer costcenter na userach, bo dostali poprawki do
 * numerów, a transactionlog ma stare". SPS przepisuje MPK do wydania raz, w chwili pobrania, więc
 * poprawka na osobie nie cofa się do jej historii. Jedna osoba na raz, zwykle miesiąc wstecz.
 *
 * ⚠️ DWA KROKI, JAK PRZY KOREKCIE CENY: najpierw podgląd z wierszami, zatwierdzenie dopiero po nim.
 * SPS nie wersjonuje rejestru — poprzednie MPK zostaje wyłącznie w tytule operacji.
 */

/** Wierszy podglądu na ekranie — resztę liczymy, ale nie rysujemy. */
const WIERSZY_NA_EKRANIE = 200;

type Osoba = { id: string; name: string; spsUserNumber: string; costCenter: string | null };

/** Dzień w strefie przeglądarki jako `RRRR-MM-DD` — do pól daty. */
function dzien(d: Date): string {
  const dwa = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${dwa(d.getMonth() + 1)}-${dwa(d.getDate())}`;
}

function miesiacWstecz(): string {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return dzien(d);
}

/**
 * WYBÓR PRACOWNIKA — wpisywanie JEST filtrem listy, jak przy pracowniku w raportach.
 *
 * ⚠️ `stan: 'wszyscy'`: korekta dotyczy historii, więc osoba już nieaktywna też musi dać się wybrać.
 */
function PolePracownika({
  groupId,
  wybrany,
  onWybierz,
}: {
  groupId: string;
  wybrany: Osoba | null;
  onWybierz: (o: Osoba | null) => void;
}) {
  const { t } = useI18n();
  const [wpisane, setWpisane] = useState('');
  const [otwarte, setOtwarte] = useState(false);
  const fraza = useOpoznione(wpisane, 250);

  const lista = useQuery({
    queryKey: ['employees', 'do-korekty-mpk', groupId, fraza],
    queryFn: () =>
      listEmployees({
        groupId,
        page: 1,
        pageSize: 30,
        ...(fraza.trim() !== '' ? { search: fraza.trim() } : {}),
        stan: 'wszyscy',
        onlyMultiMachine: false,
        sort: 'name',
        dir: 'asc',
      }),
    enabled: otwarte || fraza !== '',
  });

  return (
    <div className="relative">
      <Input
        id="kmpk-osoba"
        value={wpisane}
        autoComplete="off"
        placeholder={wybrany === null ? t('kmpk.szukaj') : `${wybrany.name} · ${wybrany.spsUserNumber}`}
        onChange={(e) => {
          setWpisane(e.target.value);
          setOtwarte(true);
          /** ⚠️ Pisanie unieważnia wybór — inaczej korekta dotyczyłaby osoby, której nie widać. */
          if (wybrany !== null) onWybierz(null);
        }}
        onFocus={() => setOtwarte(true)}
        onBlur={() => setOtwarte(false)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOtwarte(false);
        }}
      />
      {otwarte && (
        <div className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-border bg-[var(--popover,var(--card))] shadow-md">
          {lista.isPending && <p className="px-2.5 py-1.5 text-xs text-muted-foreground">{t('common.loading')}</p>}
          {(lista.data?.rows ?? []).map((o) => (
            /* `onMouseDown` z `preventDefault`, bo `onBlur` zamknąłby listę przed kliknięciem. */
            <button
              key={o.id}
              type="button"
              className="block w-full px-2.5 py-1.5 text-left text-xs hover:bg-accent"
              onMouseDown={(e) => {
                e.preventDefault();
                onWybierz({ id: o.id, name: o.name, spsUserNumber: o.spsUserNumber, costCenter: o.costCenter });
                setWpisane('');
                setOtwarte(false);
              }}
            >
              {o.name} <span className="text-muted-foreground">· {o.spsUserNumber}</span>
              {o.costCenter && <span className="text-muted-foreground"> · {o.costCenter}</span>}
            </button>
          ))}
          {!lista.isPending && (lista.data?.rows ?? []).length === 0 && (
            <p className="px-2.5 py-1.5 text-xs text-muted-foreground">{t('kmpk.brakOsob')}</p>
          )}
        </div>
      )}
    </div>
  );
}

export function KorektaMpk({ groupId }: { groupId: string }) {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();

  const [osoba, setOsoba] = useState<Osoba | null>(null);
  const [od, setOd] = useState(miesiacWstecz);
  const [doDnia, setDoDnia] = useState(() => dzien(new Date()));
  const [mpk, setMpk] = useState('');
  const [blad, setBlad] = useState<string | null>(null);
  const [podglad, setPodglad] = useState<PodgladKorektyMpk | null>(null);

  const podpowiedzi = useQuery({
    queryKey: ['employees', 'cost-centers', groupId],
    queryFn: () => fetchCostCenterSuggestions(groupId),
  });

  /**
   * ⚠️ KAŻDA ZMIANA PARAMETRÓW UNIEWAŻNIA PODGLĄD — inaczej dałoby się obejrzeć wiersze dla jednego
   * okresu, zmienić datę i zatwierdzić coś, czego nikt nie widział.
   */
  const zmien =
    <T,>(ustaw: (v: T) => void) =>
    (v: T) => {
      setPodglad(null);
      setBlad(null);
      ustaw(v);
    };

  /** Godziny dopisujemy tu, a nie w polach: korekta dotyczy CAŁYCH dni. */
  const zadanie = () => ({
    groupId,
    employeeId: osoba?.id ?? '',
    od: `${od} 00:00`,
    do: `${doDnia} 23:59`,
    mpk: mpk.trim(),
  });

  const pobierzPodglad = useMutation({
    mutationFn: () => podgladKorektyMpk(zadanie()),
    onSuccess: setPodglad,
    onError: (e: Error) => setBlad(e instanceof ApiError ? e.message : t('common.error')),
  });

  const zatwierdz = useMutation({
    mutationFn: () => wykonajKorekteMpk(zadanie()),
    onSuccess: (r) => {
      toast.success(t('kmpk.zrobione').replace('{n}', String(r.poprawionych)).replace('{k}', String(r.operacji)));
      setPodglad(null);
      void qc.invalidateQueries({ queryKey: ['authoring', 'operations'] });
    },
    onError: (e: Error) => setBlad(e instanceof ApiError ? e.message : t('common.error')),
  });

  const daneOk = osoba !== null && od !== '' && doDnia !== '' && od <= doDnia && mpk.trim() !== '';
  const zaStarzy = podglad?.agenciDoAktualizacji ?? [];
  const pusto = t('kmpk.pusto');

  return (
    <section className="rounded-lg border border-border bg-card p-4">
      <h2 className="text-sm font-semibold">{t('kmpk.tytul')}</h2>

      <div className="mt-3 grid items-end gap-3 md:grid-cols-[minmax(14rem,2fr)_auto_auto_minmax(10rem,1fr)_auto]">
        <Field label={t('kmpk.pracownik')} htmlFor="kmpk-osoba" required>
          <PolePracownika
            groupId={groupId}
            wybrany={osoba}
            onWybierz={zmien((o: Osoba | null) => {
              setOsoba(o);
              /** Nowe MPK to zwykle obecne MPK osoby — to ono dostało poprawkę. */
              if (o !== null) setMpk(o.costCenter ?? '');
            })}
          />
        </Field>
        <Field label={t('kmpk.od')} htmlFor="kmpk-od" required>
          <Input id="kmpk-od" type="date" value={od} onChange={(e) => zmien(setOd)(e.target.value)} />
        </Field>
        <Field label={t('kmpk.do')} htmlFor="kmpk-do" required>
          <Input id="kmpk-do" type="date" value={doDnia} onChange={(e) => zmien(setDoDnia)(e.target.value)} />
        </Field>
        <Field label={t('kmpk.mpk')} htmlFor="kmpk-mpk" required>
          <PoleCentrumKosztowego
            id="kmpk-mpk"
            wartosc={mpk}
            podpowiedzi={podpowiedzi.data?.podpowiedzi ?? []}
            onZmiana={zmien(setMpk)}
          />
        </Field>
        <Button variant="outline" disabled={!daneOk || pobierzPodglad.isPending} onClick={() => pobierzPodglad.mutate()}>
          {pobierzPodglad.isPending && <Loader2 className="animate-spin" />}
          {t('kmpk.pokaz')}
        </Button>
      </div>

      {osoba !== null && (
        <p className="mt-1 text-xs text-muted-foreground">
          {t('kmpk.mpkBiezace').replace('{mpk}', osoba.costCenter ?? pusto)}
        </p>
      )}

      {blad !== null && (
        <div className="mt-3">
          <ModalError>{blad}</ModalError>
        </div>
      )}

      {podglad !== null && (
        <div className="mt-4 space-y-3">
          {/*
            ⚠️ STARSZY AGENT: podgląd działa, zatwierdzenie nie. Agent sprzed tej wersji odrzuciłby
            łatkę, a portal pokazywałby już nowe MPK — więc przycisk czeka, aż bazy się podniosą.
          */}
          {zaStarzy.length > 0 && (
            <p className="flex items-start gap-1.5 rounded border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-2 text-xs">
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-[var(--warning)]" />
              <span>
                {t('kmpk.starszyAgent')
                  .replace('{wersja}', podglad.wymaganyAgent)
                  .replace('{lista}', zaStarzy.map((a) => `${a.nodeId} (${a.version ?? '—'})`).join(', '))}
              </span>
            </p>
          )}

          {podglad.doZmiany === 0 ? (
            <p className="text-sm">{t('kmpk.nicDoZmiany')}</p>
          ) : (
            <>
              <p className="text-sm">
                <span className="font-medium">{t('kmpk.doZmiany').replace('{n}', String(podglad.doZmiany))}</span>
                {podglad.juzPoprawne > 0 && (
                  <span className="text-muted-foreground"> · {t('kmpk.juzPoprawne').replace('{n}', String(podglad.juzPoprawne))}</span>
                )}
                {podglad.operacji > 1 && (
                  <span className="text-muted-foreground"> · {t('kmpk.operacji').replace('{n}', String(podglad.operacji))}</span>
                )}
              </p>

              <div className="max-h-96 overflow-auto rounded-md border border-border">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-card text-left text-muted-foreground">
                    <tr>
                      <th className="px-2 py-1.5 font-medium">{t('kmpk.kolData')}</th>
                      <th className="px-2 py-1.5 font-medium">{t('kmpk.kolAutomat')}</th>
                      <th className="px-2 py-1.5 font-medium">{t('kmpk.kolTowar')}</th>
                      <th className="px-2 py-1.5 text-right font-medium">{t('kmpk.kolIlosc')}</th>
                      <th className="px-2 py-1.5 font-medium">{t('kmpk.kolMpk')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {podglad.wydania.slice(0, WIERSZY_NA_EKRANIE).map((w, i) => (
                      <tr key={`${w.ts}-${i}`} className="border-t border-border/60">
                        <td className="whitespace-nowrap px-2 py-1 tabular-nums">{w.ts.slice(0, 16)}</td>
                        <td className="px-2 py-1">{w.automat}</td>
                        <td className="px-2 py-1">
                          {w.towar === null ? (
                            '—'
                          ) : (
                            <>
                              {w.towar.name} <span className="text-muted-foreground">· {w.towar.itemNumber}</span>
                            </>
                          )}
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums">{w.qty}</td>
                        <td className="whitespace-nowrap px-2 py-1 font-mono">
                          <span className="text-muted-foreground line-through">{w.mpk === null || w.mpk === '' ? pusto : w.mpk}</span>
                          {' → '}
                          <span className="font-medium">{mpk.trim()}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {podglad.wydania.length > WIERSZY_NA_EKRANIE && (
                <p className="text-xs text-muted-foreground">
                  {t('kmpk.wiecej').replace('{n}', String(podglad.wydania.length - WIERSZY_NA_EKRANIE))}
                </p>
              )}

              {/*
                ⚠️ ZATWIERDZENIE DOPIERO PO PODGLĄDZIE I TYLKO PRZY AKTUALNYCH AGENTACH. SPS nie
                wersjonuje rejestru, więc pomyłka w okresie odkręca się wyłącznie drugą korektą.
              */}
              <div className="flex justify-end">
                <Button disabled={zatwierdz.isPending || zaStarzy.length > 0} onClick={() => zatwierdz.mutate()}>
                  {zatwierdz.isPending && <Loader2 className="animate-spin" />}
                  {t('kmpk.zatwierdz').replace('{n}', String(podglad.doZmiany))}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}
